const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? '';

export class BookingApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'BookingApiError';
    this.status = status;
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };

  let res: Response;
  try {
    res = await fetch(`${API_BASE}/api${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new Error('Cannot reach the server. Please try again.');
  }

  let data: (T & { error?: string }) | undefined;
  try {
    data = (await res.json()) as T & { error?: string };
  } catch {
    throw new Error(`Server returned ${res.status} with no JSON body`);
  }

  if (!res.ok) throw new BookingApiError(data?.error ?? 'Request failed', res.status);
  return data as T;
}

// ── Types ────────────────────────────────────────────────────────────────────

export interface BookingOrgProfile {
  orgId: string;
  name: string;
  slug: string;
  logoBase64: string | null;
  acceptsCommercial: boolean;
  acceptsResidential: boolean;
  specialtyItemsEnabled?: boolean;
  stripeConnectAccountId?: string;
  bookingConfig?: {
    enabled: boolean;
    welcomeMessage?: string;
    enableLockInDate: boolean;
    enableRequestEstimate: boolean;
    enableShowPriceEstimate?: boolean;
    depositType?: 'fixed' | 'percent';
    depositAmount?: number;
    customDiscoverySources?: string[];
    brandPrimaryColor?: string;
    brandTitleTextColor?: string;
  };
}

export interface CreateLeadResponse {
  bookingToken: string;
  leadId: string;
}

export interface EstimateResponse {
  estimatedValue: number | null;
  reason?: string;
  rangePercent?: number;
  rangeLow?: number;
  rangeHigh?: number;
}

export interface PlaceSuggestion {
  description: string;
  placeId: string;
}

// ── API methods ──────────────────────────────────────────────────────────────

export const bookingApi = {
  placesAutocomplete: (input: string) =>
    request<{ success: boolean; data: PlaceSuggestion[] }>('GET', `/booking/places/autocomplete?input=${encodeURIComponent(input)}`),

  getOrgProfile: (slug: string) =>
    request<{ success: boolean; data: BookingOrgProfile }>('GET', `/booking/${slug}`),

  createLead: (slug: string, body: { email?: string; phone?: string; name?: string; type?: string }) =>
    request<{ success: boolean; data: CreateLeadResponse }>('POST', `/booking/${slug}/leads`, body),

  updateLead: (slug: string, bookingToken: string, body: Record<string, unknown>) =>
    request<{ success: boolean; data: { updated: boolean } }>('PATCH', `/booking/${slug}/leads/${bookingToken}`, body),

  calculateEstimate: (slug: string, bookingToken: string) =>
    request<{ success: boolean; data: EstimateResponse }>('POST', `/booking/${slug}/leads/${bookingToken}/estimate`),

  recordAction: (slug: string, bookingToken: string, action: 'lock_in_date' | 'request_estimate') =>
    request<{ success: boolean }>('POST', `/booking/${slug}/leads/${bookingToken}/action`, { action }),

  createDeposit: (slug: string, bookingToken: string) =>
    request<{ success: boolean; data: { clientSecret: string; amountCents: number } }>('POST', `/booking/${slug}/leads/${bookingToken}/deposit`),
};
